import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

export type NotificationTab = "buy_ongoing" | "buy_done" | "sell_listings" | "sell_ongoing" | "sell_done" | "disputes";
export type NotificationCounts = Record<NotificationTab, number>;

const EMPTY: NotificationCounts = { buy_ongoing: 0, buy_done: 0, sell_listings: 0, sell_ongoing: 0, sell_done: 0, disputes: 0 };
const EVENT = "hd-notification-counts";
let current: NotificationCounts = EMPTY;
let activeTabs = new Set<NotificationTab>();

const publish = (c: NotificationCounts) => {
  current = c;
  window.dispatchEvent(new CustomEvent(EVENT));
};

export const refreshNotificationCounts = async () => {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) return publish(EMPTY);
  // Flikar som är öppna just nu räknas som lästa
  await Promise.all([...activeTabs].filter((t) => SEEN_ON_VIEW.includes(t)).map((t) => supabase.rpc("mark_notification_seen", { _tab: t })));
  const { data, error } = await supabase.rpc("get_notification_counts");
  if (error || !data) return;
  publish({ ...EMPTY, ...(data as Partial<NotificationCounts>) });
};

// Endast informationsnotiser försvinner vid visning; övriga försvinner när ärendet är hanterat.
const SEEN_ON_VIEW: NotificationTab[] = ["sell_listings", "sell_done"];

export const markNotificationSeen = async (tab: NotificationTab) => {
  if (!SEEN_ON_VIEW.includes(tab)) return;
  publish({ ...current, [tab]: 0 });
  await supabase.rpc("mark_notification_seen", { _tab: tab });
};

/** Läser antal olästa notiser per flik och uppdaterar automatiskt. */
export function useNotificationCounts() {
  const [counts, setCounts] = useState<NotificationCounts>(current);

  useEffect(() => {
    const onChange = () => setCounts(current);
    window.addEventListener(EVENT, onChange);
    refreshNotificationCounts();
    const interval = window.setInterval(refreshNotificationCounts, 30000);
    const onFocus = () => refreshNotificationCounts();
    window.addEventListener("focus", onFocus);
    const { data: sub } = supabase.auth.onAuthStateChange(() => refreshNotificationCounts());
    return () => {
      window.removeEventListener(EVENT, onChange);
      window.removeEventListener("focus", onFocus);
      window.clearInterval(interval);
      sub.subscription.unsubscribe();
    };
  }, []);

  const total = Object.values(counts).reduce((a, b) => a + b, 0);
  return { counts, total };
}

/** Markerar en flik som läst medan den är öppen. */
export function useMarkTabSeen(tab: NotificationTab | null) {
  useEffect(() => {
    if (!tab) return;
    activeTabs.add(tab);
    markNotificationSeen(tab);
    return () => {
      activeTabs.delete(tab);
      markNotificationSeen(tab);
    };
  }, [tab]);
}

